
















































































import { Component, Prop, Vue } from "vue-property-decorator";
import {
  COLOR_COCKPIT_MANAGEMENT,
  COLOR_COCKPIT_MANAGEMENT_DARK,
  COLOR_MIRA_BAR,
  COLOR_MIRA_BAR_DARK,
  COLOR_MIRA_CARD_HOVER_DARK,
} from "@/utils/colors";
import { MiraCoverageStatus } from "@/utils/MiraDataQuality";
import { MiraTile, miraStatusColor } from "@/utils/MiraDataQualityView";
import { MiraSectionAvailability } from "@/utils/MiraOverview";
import { themed } from "@/utils/Theme";
import { formatNumber } from "@/utils/Utils";

// The same icons as the status chips on the Datenqualität screen, so a shape means one thing.
const STATUS_ICON: Record<MiraCoverageStatus, string> = {
  [MiraCoverageStatus.GOOD]: "mdi-check-circle",
  [MiraCoverageStatus.WARNING]: "mdi-alert",
  [MiraCoverageStatus.CRITICAL]: "mdi-alert-octagon",
};

/**
 * One section's card on the Mira landing page, a square like the Cockpit's tiles (Hans, #2610102):
 * what the section covers, up to six of its figures, and at the bottom the gear to its settings beside
 * how many figures the card can show ("available/all", `cardAvailability`; the words are in its tooltip
 * and in the settings, `MiraLandingFiguresDialog`).
 *
 * A card whose section has a detail screen opens it; the others say "Detailansicht folgt" and are
 * not clickable, so nothing on the page leads nowhere. The gear never opens the screen. A status is
 * an icon whose shape differs per state, with the word as its tooltip and screen-reader label.
 */
@Component
export default class MiraSectionCardComponent extends Vue {
  @Prop({ required: true })
  icon!: string;

  @Prop({ required: true })
  title!: string;

  @Prop({ required: true })
  description!: string;

  @Prop({ required: true })
  availability!: MiraSectionAvailability;

  @Prop({ default: false })
  opens!: boolean;

  @Prop({ default: () => [] })
  figures!: MiraTile[];

  /** "Markt 29 %" per figure id, where the Markt comparison is chosen and the figure has a Markt value. */
  @Prop({ default: () => ({}) })
  marketTexts!: Record<string, string>;

  /**
   * The clickable card lifts to the raised surface on hover in dark mode, where a shadow barely
   * shows; `undefined` leaves Vuetify's own surface, so light mode is unchanged.
   */
  cardColor(hover: boolean): string | undefined {
    return hover && this.opens && this.$vuetify.theme.dark ? COLOR_MIRA_CARD_HOVER_DARK : undefined;
  }

  get iconBackground() {
    return themed(COLOR_COCKPIT_MANAGEMENT, COLOR_COCKPIT_MANAGEMENT_DARK);
  }

  get iconColor() {
    return themed(COLOR_MIRA_BAR, COLOR_MIRA_BAR_DARK);
  }

  /** "available/all": a ratio, so no spaces around the slash. */
  get ratio() {
    const { available, waiting } = this.availability;
    return `${formatNumber(available)}/${formatNumber(available + waiting)}`;
  }

  /** The ratio in words, for a touch screen: "3 von 5 Kennzahlen". */
  get ratioLong() {
    const { available, waiting } = this.availability;
    return this.$t("mira.landing.ratioLong", [formatNumber(available), formatNumber(available + waiting)]).toString();
  }

  get availabilityText() {
    const available = this.$tc("mira.landing.available", this.availability.available).toString();
    return this.availability.waiting > 0
      ? `${available}, ${this.$tc("mira.landing.waiting", this.availability.waiting)}`
      : available;
  }

  statusColor(status: MiraCoverageStatus) {
    return miraStatusColor(status, this.$vuetify.theme.dark);
  }

  statusIcon(status: MiraCoverageStatus) {
    return STATUS_ICON[status];
  }

  onOpen() {
    this.$emit("open");
  }

  onSettings() {
    this.$emit("settings");
  }
}
